/**
 * Plan de pagos de una factura de Cuentas por Pagar (pestaña del detalle).
 *
 * El plan reparte el balance de la factura en cuotas con fecha. Los pagos se
 * siguen registrando contra la factura en "Pagos a suplidores" y el backend
 * los reparte solos sobre las cuotas, de la primera a la última; aquí solo se
 * arma, rehace o quita el plan. Las cuotas tienen que sumar el balance.
 *
 * Si la factura cambia después (devolución de un chasis, otro costo, un pago
 * anterior anulado) el plan queda "desfasado" y se avisa para rehacerlo.
 */
import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useGlobalMDAlert } from "context";
import { FP, fpInputSx, fpDataGridSx, StatusPill, ConfirmarDialog } from "components/financepro";
import { useApi } from "lib/api";

const toNum = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};
const r2 = (n) => Math.round(toNum(n) * 100) / 100;
const fmtMoney = (v) =>
  toNum(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: bg, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

export const FRECUENCIAS = [
  { value: "mensual", label: "Mensual" },
  { value: "quincenal", label: "Quincenal" },
  { value: "semanal", label: "Semanal" },
];

const pad = (n) => String(n).padStart(2, "0");
const isoLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const deIso = (s) => {
  const [y, m, d] = String(s)
    .split("-")
    .map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d);
};

/** Fecha de la cuota i (0 = la primera). Mensual conserva el día y lo
 *  recorta al fin de mes (31 ene -> 28 feb -> 31 mar). */
export function fechaCuota(primera, frecuencia, i) {
  const base = deIso(primera);
  if (frecuencia === "semanal")
    return isoLocal(new Date(base.getFullYear(), base.getMonth(), base.getDate() + 7 * i));
  if (frecuencia === "quincenal")
    return isoLocal(new Date(base.getFullYear(), base.getMonth(), base.getDate() + 15 * i));
  const y = base.getFullYear();
  const m = base.getMonth() + i;
  const ultimo = new Date(y, m + 1, 0).getDate();
  return isoLocal(new Date(y, m, Math.min(base.getDate(), ultimo)));
}

/** Reparte el balance en `cantidad` cuotas iguales; los centavos que sobran
 *  van a la última para que la suma cuadre exacto. */
export function generarCuotas(balance, cantidad, frecuencia, primera) {
  const n = Math.max(1, Math.min(120, parseInt(cantidad, 10) || 1));
  const totalCent = Math.round(toNum(balance) * 100);
  const cuota = Math.floor(totalCent / n);
  return Array.from({ length: n }, (_v, i) => ({
    fecha: fechaCuota(primera, frecuencia, i),
    monto: ((i === n - 1 ? totalCent - cuota * (n - 1) : cuota) / 100).toFixed(2),
  }));
}

export function manana() {
  const d = new Date();
  return isoLocal(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1));
}

const PILL_ESTADO = {
  pagada: { label: "Pagada", variant: "success" },
  parcial: { label: "Abonada", variant: "info" },
  vencida: { label: "Vencida", variant: "danger" },
  pendiente: { label: "Pendiente", variant: "neutral" },
};

function Dato({ label, value, color }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.68rem", color: FP.color.textMuted, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: color || FP.color.textPrimary }}>
        {value}
      </Typography>
    </Box>
  );
}

/** Por qué no se puede guardar el plan, o null si está bien. */
export function problemaCuotas(cuotas, balance) {
  if (!cuotas.length) return "Indique al menos una cuota.";
  if (cuotas.some((c) => !c.fecha || toNum(c.monto) <= 0))
    return "Cada cuota necesita fecha y un monto mayor que 0.";
  if (cuotas.some((c, i) => i > 0 && c.fecha < cuotas[i - 1].fecha))
    return "Las fechas deben ir en orden, de la primera cuota a la última.";
  const dif = r2(r2(balance) - r2(cuotas.reduce((a, c) => a + toNum(c.monto), 0)));
  if (Math.abs(dif) > 0.01)
    return `Las cuotas no cuadran con el total: ${dif > 0 ? "faltan" : "sobran"} RD$ ${fmtMoney(
      Math.abs(dif)
    )}.`;
  return null;
}

/**
 * Generador + lista editable de cuotas (controlado: `cuotas`/`setCuotas`).
 * Lo usan el diálogo del detalle y la compra en lote (inventario bulk).
 */
export function EditorCuotas({
  balance,
  cuotas,
  setCuotas,
  disabled = false,
  etiquetaTotal = "el balance",
}) {
  const [cantidad, setCantidad] = useState(String(cuotas.length || 3));
  const [frecuencia, setFrecuencia] = useState("mensual");
  const [primera, setPrimera] = useState(cuotas[0]?.fecha || manana());
  const guardando = disabled;

  const total = r2(cuotas.reduce((a, c) => a + toNum(c.monto), 0));
  const diferencia = r2(r2(balance) - total);
  const fueraDeOrden = cuotas.some((c, i) => i > 0 && c.fecha < cuotas[i - 1].fecha);

  const setCuota = (i, campo, valor) =>
    setCuotas((prev) => prev.map((c, j) => (j === i ? { ...c, [campo]: valor } : c)));

  const agregar = () =>
    setCuotas((prev) => {
      const ultima = prev[prev.length - 1];
      const fecha = ultima ? fechaCuota(ultima.fecha, frecuencia, 1) : primera;
      return [...prev, { fecha, monto: Math.max(diferencia, 0).toFixed(2) }];
    });

  return (
    <Box>
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={4} sm={2.5}>
          <TextField
            label="Cuotas"
            type="number"
            size="small"
            fullWidth
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
            inputProps={{ min: 1, max: 120 }}
            sx={fpInputSx}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={8} sm={3.5}>
          <TextField
            select
            label="Frecuencia"
            size="small"
            fullWidth
            value={frecuencia}
            onChange={(e) => setFrecuencia(e.target.value)}
            sx={fpInputSx}
            InputLabelProps={{ shrink: true }}
          >
            {FRECUENCIAS.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={7} sm={3.5}>
          <TextField
            label="Primera cuota"
            type="date"
            size="small"
            fullWidth
            value={primera}
            onChange={(e) => setPrimera(e.target.value)}
            sx={fpInputSx}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={5} sm={2.5}>
          <Button
            fullWidth
            startIcon={<AutorenewIcon />}
            disabled={!primera || guardando}
            onClick={() => setCuotas(generarCuotas(balance, cantidad, frecuencia, primera))}
            sx={btnOutline(FP.color.navy)}
          >
            Generar
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, border: `1px solid ${FP.color.border}`, borderRadius: 2, p: 1.2 }}>
        {cuotas.map((c, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ py: 0.6 }}>
            <Typography sx={{ width: 28, fontWeight: 800, color: FP.color.textSecondary }}>
              {i + 1}
            </Typography>
            <TextField
              type="date"
              size="small"
              value={c.fecha}
              onChange={(e) => setCuota(i, "fecha", e.target.value)}
              sx={{ ...fpInputSx, flex: 1 }}
            />
            <TextField
              type="number"
              size="small"
              value={c.monto}
              onChange={(e) => setCuota(i, "monto", e.target.value)}
              inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
              sx={{ ...fpInputSx, flex: 1 }}
            />
            <IconButton
              size="small"
              aria-label="Quitar cuota"
              disabled={cuotas.length <= 1 || guardando}
              onClick={() => setCuotas((prev) => prev.filter((_x, j) => j !== i))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={agregar}
          disabled={guardando || cuotas.length >= 120}
          sx={{ ...btnBase, mt: 0.5, color: FP.color.navy }}
        >
          Agregar cuota
        </Button>
      </Box>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.5, px: 0.5 }}>
        <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
          Total de cuotas: RD$ {fmtMoney(total)}
        </Typography>
        <Typography
          sx={{
            fontWeight: 800,
            color: Math.abs(diferencia) > 0.01 ? FP.color.danger : FP.color.successDark,
          }}
        >
          {Math.abs(diferencia) > 0.01
            ? `${diferencia > 0 ? "Faltan" : "Sobran"} RD$ ${fmtMoney(Math.abs(diferencia))}`
            : `Cuadra con ${etiquetaTotal}`}
        </Typography>
      </Stack>
      {fueraDeOrden && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Las fechas deben ir en orden, de la primera cuota a la última.
        </Alert>
      )}
    </Box>
  );
}

function EditorPlan({ abierto, balance, planActual, onCerrar, onGuardado, facturaId }) {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const [cuotas, setCuotas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  // Clave para reiniciar el editor (cantidad, frecuencia, primera) en cada apertura.
  const [apertura, setApertura] = useState(0);

  // Al abrir: parte del plan vigente si cuadra con el balance; si no, genera.
  React.useEffect(() => {
    if (!abierto) return;
    const vigentes = (planActual?.cuotas || []).filter((c) => toNum(c.pendiente) > 0);
    const sumaVigente = r2(vigentes.reduce((a, c) => a + toNum(c.pendiente), 0));
    if (vigentes.length && Math.abs(sumaVigente - r2(balance)) <= 0.01) {
      setCuotas(vigentes.map((c) => ({ fecha: c.fecha, monto: toNum(c.pendiente).toFixed(2) })));
    } else {
      setCuotas(generarCuotas(balance, 3, "mensual", manana()));
    }
    setApertura((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  const invalida = problemaCuotas(cuotas, balance) !== null;

  const guardar = async () => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/cxp_plan_guardar", {
        id: facturaId,
        cuotas: cuotas.map((c) => ({ fecha: c.fecha, monto: r2(c.monto) })),
      });
      if (ok) {
        showAlert({ message: data.ok || "Plan de pagos guardado", color: "success" });
        onGuardado();
      } else {
        showAlert({ message: data.Error || "No se pudo guardar el plan", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open={abierto}
      onClose={() => (guardando ? null : onCerrar())}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        {planActual ? "Rehacer plan de pagos" : "Plan de pagos"}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.86rem", mb: 2 }}>
          Reparta el balance de RD$ {fmtMoney(balance)} en cuotas. Lo ya pagado no entra en el plan;
          los pagos que se registren desde ahora se aplican a las cuotas en orden.
        </Typography>
        {cuotas.length > 0 && (
          <EditorCuotas
            key={apertura}
            balance={balance}
            cuotas={cuotas}
            setCuotas={setCuotas}
            disabled={guardando}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCerrar} disabled={guardando} sx={btnOutline(FP.color.navy)}>
          Cancelar
        </Button>
        <Button
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={guardar}
          disabled={invalida || guardando}
          sx={btnSolid(FP.color.navy)}
        >
          {guardando ? "Guardando…" : "Guardar plan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PlanPagosCxp({ factura, plan, puedeEditar, onCambio }) {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const [editor, setEditor] = useState(false);
  const [confirmarQuitar, setConfirmarQuitar] = useState(false);
  const [quitando, setQuitando] = useState(false);

  const balance = toNum(factura?.balance);
  const bloqueo = factura?.anulada
    ? "La factura está anulada."
    : factura?.consignada
    ? "Las consignaciones no llevan plan de pagos: su valor cambia con cada motor vendido."
    : null;
  const editable = puedeEditar && !bloqueo && balance > 0.01;

  const columnas = useMemo(
    () => [
      { field: "numero", headerName: "#", width: 70 },
      { field: "fechatexto", headerName: "Vence", width: 120 },
      {
        field: "monto",
        headerName: "Monto",
        width: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (v) => fmtMoney(v),
      },
      {
        field: "aplicado",
        headerName: "Pagado",
        width: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (v) => fmtMoney(v),
      },
      {
        field: "pendiente",
        headerName: "Pendiente",
        width: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (v) => fmtMoney(v),
      },
      {
        field: "estado",
        headerName: "Estado",
        flex: 1,
        minWidth: 170,
        sortable: false,
        renderCell: (p) => {
          const e = PILL_ESTADO[p.value] || PILL_ESTADO.pendiente;
          return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
              <StatusPill label={e.label} variant={e.variant} />
              {p.value === "vencida" && (
                <Typography sx={{ fontSize: "0.75rem", color: FP.color.danger, fontWeight: 700 }}>
                  {p.row.dias} días
                </Typography>
              )}
            </Stack>
          );
        },
      },
    ],
    []
  );

  const quitar = async () => {
    setQuitando(true);
    try {
      const { ok, data } = await post("/api/cxp_plan_eliminar", { id: factura.id });
      if (ok) {
        setConfirmarQuitar(false);
        showAlert({ message: data.ok || "Plan de pagos eliminado", color: "success" });
        onCambio();
      } else {
        showAlert({ message: data.Error || "No se pudo quitar el plan", color: "error" });
      }
    } finally {
      setQuitando(false);
    }
  };

  const editorDialog = (
    <EditorPlan
      abierto={editor}
      balance={balance}
      planActual={plan}
      facturaId={factura?.id}
      onCerrar={() => setEditor(false)}
      onGuardado={() => {
        setEditor(false);
        onCambio();
      }}
    />
  );

  if (!plan) {
    return (
      <Stack
        alignItems="center"
        spacing={1.4}
        sx={{ py: 6, color: FP.color.textMuted, textAlign: "center" }}
      >
        <EventNoteOutlinedIcon sx={{ fontSize: 44 }} />
        <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
          Esta factura no tiene plan de pagos
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", maxWidth: 460 }}>
          {bloqueo ||
            (balance <= 0.01
              ? "La factura no tiene balance pendiente."
              : "Reparta el balance en cuotas con fecha para saber qué vence y cuándo pagarle al suplidor.")}
        </Typography>
        {editable && (
          <Button
            startIcon={<EventNoteOutlinedIcon />}
            onClick={() => setEditor(true)}
            sx={btnSolid(FP.color.navy)}
          >
            Crear plan de pagos
          </Button>
        )}
        {editorDialog}
      </Stack>
    );
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={6} sm={3} md={2}>
          <Dato label="TOTAL DEL PLAN" value={fmtMoney(plan.total)} />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Dato label="PAGADO AL PLAN" value={fmtMoney(plan.aplicado)} />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Dato label="PENDIENTE" value={fmtMoney(plan.pendiente)} />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Dato
            label={`VENCIDO${plan.cuotasvencidas ? ` (${plan.cuotasvencidas})` : ""}`}
            value={fmtMoney(plan.vencido)}
            color={toNum(plan.vencido) > 0 ? FP.color.danger : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Dato
            label="PRÓXIMA CUOTA"
            value={
              plan.proxima
                ? `${plan.proxima.fechatexto} · ${fmtMoney(plan.proxima.pendiente)}`
                : "—"
            }
          />
        </Grid>
        <Grid item xs={12} md={2}>
          {editable && (
            <Stack direction={{ xs: "row", md: "column" }} spacing={1}>
              <Button size="small" onClick={() => setEditor(true)} sx={btnOutline(FP.color.navy)}>
                Rehacer plan
              </Button>
              <Button
                size="small"
                onClick={() => setConfirmarQuitar(true)}
                sx={btnOutline(FP.color.danger)}
              >
                Quitar plan
              </Button>
            </Stack>
          )}
        </Grid>
      </Grid>

      {plan.desfasado && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          El plan ya no cuadra con la factura: el balance es RD$ {fmtMoney(balance)} y al plan le
          quedan RD$ {fmtMoney(plan.pendiente)} (diferencia RD$ {fmtMoney(plan.diferencia)}). Cambió
          el valor de la factura o se anuló un pago anterior al plan.
          {editable ? " Use “Rehacer plan” para ajustarlo." : ""}
        </Alert>
      )}

      <Box sx={{ bgcolor: "#fff" }}>
        <DataGrid
          autoHeight
          rows={plan.cuotas}
          columns={columnas}
          getRowId={(r) => r.numero}
          rowHeight={44}
          pageSizeOptions={[12, 24, 60]}
          initialState={{ pagination: { paginationModel: { pageSize: 12 } } }}
          sx={fpDataGridSx}
          disableRowSelectionOnClick
        />
      </Box>
      <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.78rem", mt: 1.2, px: 0.5 }}>
        Los pagos se registran en Pagos a suplidores y se aplican a las cuotas en orden.
        {plan.creado
          ? ` Plan hecho por ${plan.creado}${plan.fechacre ? ` el ${plan.fechacre}` : ""}.`
          : ""}
      </Typography>

      {editorDialog}
      <ConfirmarDialog
        abierto={confirmarQuitar}
        titulo="Quitar plan de pagos"
        mensaje="La factura y sus pagos no cambian; solo se borran las cuotas del plan."
        textoConfirmar="Quitar plan"
        destructivo
        cargando={quitando}
        onCerrar={() => setConfirmarQuitar(false)}
        onConfirmar={quitar}
      />
    </Box>
  );
}
